import { createGlobalStyle } from 'styled-components';
import fonts from './fonts';
import variables from './variables';
import TransitionStyles from './TransitionStyles';
import PrismStyles from './PrismStyles';
import PreloaderStyles from './PreloaderStyles';

const GlobalStyle = createGlobalStyle`
  ${fonts};
  ${variables};

  html {
    box-sizing: border-box;
    width: 100%;
    scroll-behavior: smooth;
    overflow-x: hidden;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  ::selection {
    background-color: var(--lightest-navy);
    color: var(--lightest-slate);
  }

  /* Provide basic, default focus styles.*/
  :focus {
    outline: 2px dashed var(--green);
    outline-offset: 3px;
  }

  /*
    Remove default focus styles for mouse users ONLY if
    :focus-visible is supported on this platform.
  */
  :focus:not(:focus-visible) {
    outline: none;
    outline-offset: 0px;
  }

  /*
    Optionally: If :focus-visible is supported on this
    platform, provide enhanced focus styles for keyboard
    focus.
  */
  :focus-visible {
    outline: 2px dashed var(--green);
    outline-offset: 3px;
  }

  /* Scrollbar Styles */
  html {
    scrollbar-width: thin;
    scrollbar-color: var(--dark-slate) var(--navy);
  }
  ::-webkit-scrollbar {
    width: 12px;
  }
  ::-webkit-scrollbar-track {
    background: var(--navy);
  }
  ::-webkit-scrollbar-thumb {
    background-color: var(--dark-slate);
    border: 3px solid var(--navy);
    border-radius: 10px;
  }

  body {
    margin: 0;
    width: 100%;
    min-height: 100%;
    overflow-x: hidden;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;
    background-color: #08080c;
    background-image: 
      linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
      linear-gradient(180deg, #08080c 0%, #12121a 100%);
    background-size: 40px 40px, 40px 40px, 100% 100%;
    background-attachment: fixed;
    color: var(--slate);
    font-family: var(--font-sans);
    font-size: var(--fz-xl);
    line-height: 1.6;
    position: relative;

    @media (hover: hover) and (pointer: fine) {
      background-color: #020203;
      background-image: 
        linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px),
        linear-gradient(180deg, #020203 0%, #050507 100%);
    }

    @media (max-width: 480px) {
      font-size: var(--fz-lg);
    }

    &.hidden {
      overflow: hidden;
    }

    &.blur {
      #content > * {
        filter: blur(10px) brightness(0.5);
        transition: var(--transition);
        pointer-events: none;
        user-select: none;
      }
    }

    /* Moving fog in background - ONLY enabled on computers (desktop/laptop screens) with pointer/hover support */
    @media (hover: hover) and (pointer: fine) {
      &::before {
        content: '';
        position: fixed;
        top: -50%;
        left: -50%;
        width: 200%;
        height: 200%;
        background: 
          radial-gradient(circle at 30% 30%, rgba(255, 42, 42, 0.12), transparent 40%),
          radial-gradient(circle at 70% 70%, rgba(29, 78, 216, 0.12), transparent 40%),
          radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.02), transparent 30%);
        animation: fog-drift 40s linear infinite;
        pointer-events: none;
        z-index: -2;
      }

      /* Vignette blur on top for real fog effect */
      &::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        backdrop-filter: blur(8px);
        -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 25%, black 100%);
        mask-image: radial-gradient(circle at 50% 50%, transparent 25%, black 100%);
        pointer-events: none;
        z-index: 9999;
      }
    }
  }

  @keyframes fog-drift {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  main {
    margin: 0 auto;
    width: 100%;
    max-width: 1600px;
    min-height: 100vh;
    padding: 20px 50px 0;

    @media (max-width: 1080px) {
      padding: 20px 40px 0;
    }
    @media (max-width: 768px) {
      padding: 10px 30px 0;
    }
    @media (max-width: 480px) {
      padding: 20px 20px 0;
    }

    &.fillHeight {
      padding: 0 150px;

      @media (max-width: 1080px) {
        padding: 0 100px;
      }
      @media (max-width: 768px) {
        padding: 0 50px;
      }
      @media (max-width: 480px) {
        padding: 0 25px;
      }
    }
  }

  section {
    margin: 0 auto;
    padding: clamp(60px, 8vh, 100px) 0;
    max-width: 1000px;
    scroll-margin-top: clamp(40px, 6vh, 80px);

    @media (max-width: 768px) {
      padding: clamp(40px, 6vh, 60px) 0;
      min-height: 100vh;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    @media (max-width: 1080px) and (orientation: landscape) and (hover: none), (max-height: 600px) and (hover: none) {
      padding: 30px 0;
      scroll-margin-top: 40px;
      min-height: auto;
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 10px 0;
    font-weight: 800;
    color: var(--white);
    line-height: 1.1;
  }

  .big-heading {
    margin: 0;
    font-size: clamp(50px, 10vw, 100px); // Much larger
    letter-spacing: -2px;
  }

  .medium-heading {
    margin: 0;
    font-size: clamp(40px, 8vw, 70px);
    letter-spacing: -1px;
  }

  .numbered-heading {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    margin: 10px 0 40px;
    padding-left: 20px;
    width: 100%;
    font-size: clamp(28px, 5vw, 40px);
    white-space: nowrap;
    font-weight: 800;
    letter-spacing: -0.5px;
    background: linear-gradient(90deg, #ffffff 0%, #ff2a2a 100%);
    color: transparent !important;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    border-left: 5px solid var(--green);
    padding-top: 5px;
    padding-bottom: 5px;

    @media (max-width: 768px) {
      white-space: normal;

      &:after {
        min-width: 60px;
      }
    }

    &:after {
      content: '';
      display: block;
      flex-grow: 1;
      max-width: 300px;
      height: 1px;
      margin-left: 20px;
      background-color: rgba(255, 255, 255, 0.4);
    }
  }

  img,
  svg,
  .gatsby-image-wrapper {
    width: 100%;
    max-width: 100%;
    vertical-align: middle;
  }

  img[alt=""],
  img:not([alt]) {
    filter: blur(5px);
  }

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
    vertical-align: middle;

    &.feather {
      fill: none;
    }
  }

  a {
    display: inline-block;
    text-decoration: none;
    text-decoration-skip-ink: auto;
    color: inherit;
    position: relative;
    transition: var(--transition);

    &:hover,
    &:focus {
      color: var(--green);
    }

    &.inline-link {
      ${({ theme }) => theme.mixins.inlineLink};
    }
  }

  button {
    cursor: pointer;
    border: 0;
    border-radius: 0;
  }

  input, textarea {
    border-radius: 0;
    outline: 0;

    &:focus {
      outline: 0;
    }
    &:focus,
    &:active {
      &::placeholder {
        opacity: 0.5;
      }
    }
  }

  p {
    margin: 0 0 15px 0;

    &:last-child,
    &:last-of-type {
      margin: 0;
    }

    & > a {
      ${({ theme }) => theme.mixins.inlineLink};
    }

    & > code {
      background-color: var(--light-navy);
      color: var(--white);
      font-size: var(--fz-sm);
      border-radius: var(--border-radius);
      padding: 0.3em 0.5em;
    }
  }

  ul {
    &.fancy-list {
      padding: 0;
      margin: 0;
      list-style: none;
      font-size: var(--fz-lg);
      li {
        position: relative;
        padding-left: 30px;
        margin-bottom: 10px;
        &:before {
          content: '▹';
          position: absolute;
          left: 0;
          color: var(--green);
        }
      }
    }
  }

  blockquote {
    border-left-color: var(--green);
    border-left-style: solid;
    border-left-width: 1px;
    margin-left: 0px;
    margin-right: 0px;
    padding-left: 1.5rem;

    p {
      font-style: italic;
      font-size: 24px;
    }
  }

  hr {
    background-color: var(--lightest-navy);
    height: 1px;
    border-width: 0px;
    border-style: initial;
    border-color: initial;
    border-image: initial;
    margin: 1rem;
  }

  code {
    font-family: var(--font-mono);
    font-size: var(--fz-md);
  }

  .skip-to-content {
    ${({ theme }) => theme.mixins.button};
    position: absolute;
    top: auto;
    left: -999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
    z-index: -99;

    &:focus,
    &:active {
      background-color: var(--green);
      color: var(--navy);
      top: 0;
      left: 0;
      width: auto;
      height: auto;
      overflow: auto;
      z-index: 99;
    }
  }

  #logo {
    color: var(--green);
  }

  .overline {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-md);
    font-weight: 400;
  }

  .subtitle {
    color: var(--green);
    margin: 0 0 20px 0;
    font-size: var(--fz-md);
    font-family: var(--font-mono);
    font-weight: 400;
    line-height: 1.5;
    @media (max-width: 1080px) {
      font-size: var(--fz-sm);
    }
    @media (max-width: 768px) {
      font-size: var(--fz-xs);
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      line-height: 1.5;
    }
  }

  .breadcrumb {
    display: flex;
    align-items: center;
    margin-bottom: 50px;
    color: var(--green);

    .arrow {
      display: block;
      margin-right: 10px;
      padding-top: 4px;
    }

    a {
      ${({ theme }) => theme.mixins.inlineLink};
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      font-weight: 600;
      line-height: 1.5;
      text-transform: uppercase;
      letter-spacing: 0.1em;
    }
  }

  .gatsby-image-outer-wrapper {
    height: 100%;
  }

  /* ==========================================================================
     PREMIUM UNIFIED MOBILE GLASS CARDS SYSTEM
     ========================================================================== */
  @media (max-width: 768px) {
    /* BASE LAYOUT PROPERTIES (Always apply) */
    .hero-card,
    .bento-card,
    .project-inner,
    .blog-inner,
    .service-inner,
    .contact-card,
    div[class*="StyledProjectCard"],
    #workstation div[class*="HardwareCard"],
    #workstation div[class*="ToolCard"] {
      padding: 24px 20px !important;
      border-radius: 16px !important;
      backdrop-filter: blur(12px) !important;
      -webkit-backdrop-filter: blur(12px) !important;
    }

    .card-image {
      padding: 0 !important;
    }

    .hero-card {
      padding: 24px 35px !important;
    }

    /* Target Folder & Brand Icons inside all cards */
    .folder svg, 
    .tool-icon-wrapper svg,
    .tool-icon-wrapper img,
    .icon-gauge,
    .os-logo-container svg,
    .os-logo-container img {
      width: 28px !important;
      height: 28px !important;
      min-width: 28px !important;
      min-height: 28px !important;
    }

      /* Card Top Labels */
      .label, .project-overline, .topology-tag, .overline {
        font-size: 10px !important;
        letter-spacing: 1.5px !important;
        margin-bottom: 8px !important;
      }



      /* Spacing between top elements and title */
      .project-top, .blog-top, .service-top, .card-top {
        margin-bottom: 16px !important;
      }

      /* Card Body Paragraphs & Details */
      p, .details, .project-description, .blog-description, .service-description, p.details {
        font-size: 13px !important;
        line-height: 1.5 !important;
        margin: 0 0 15px 0 !important;

        b, strong {
          font-weight: 600 !important;
          color: var(--green) !important;
        }
      }

      /* Card Footers, Tags, and Pills */
      .card-footer, .project-tech-list, .skills-pills, .blog-tech-list, .tags {
        margin-top: auto !important;
        gap: 6px !important;
        display: flex !important;
        flex-wrap: wrap !important;
        padding: 0 !important;
        list-style: none !important;

        span.tag, li, .pill, span {
          font-size: 10px !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          font-family: var(--font-mono) !important;
          background: rgba(255, 255, 255, 0.03) !important;
          border: 1px solid rgba(255, 255, 255, 0.06) !important;
          color: var(--light-slate) !important;
          line-height: 1 !important;
          margin: 0 !important;
          transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1) !important;

          &:hover {
            background: #ff2a2a !important;
            color: #08080c !important;
            border-color: #ff2a2a !important;
            transform: scale(1.1) !important;
            cursor: pointer !important;
          }
        }
      }
    }

    /* Specific custom overrides for better contact card aesthetics */
    .contact-card {
      padding: 40px 20px !important;

      h2.title {
        font-size: 26px !important;
        margin-bottom: 12px !important;
      }
    }

    /* Adjust padding of specific project-card container inner layers */
    div[class*="StyledProjectCard"] {
      padding: 0 !important;
    }
    div[class*="StyledProjectCard"] .project-content {
      padding: 24px 20px !important;
    }
  }

  ${TransitionStyles};

  ${PrismStyles};

  ${PreloaderStyles};
`;

export default GlobalStyle;
